import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Box,
  FormControl,
  styled,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { setStateSelection } from '../../../redux/actions/WizardLaunchActions';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import './StateSelection.scss';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Disclaimers from '../../../assets/img/disclaimer-icon.svg';

const CustomAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto',
  '& .MuiOutlinedInput-root': {
    height: '64px',
    backgroundColor: '#F1F2F4',
    borderRadius: '12px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '0 22px !important',
    fontSize: '16px',
    color: '#000',
    fontFamily: 'Barlow',
    fontWeight: 500,
    lineHeight: 'normal',
    '&::placeholder': {
      color: '#000',
      opacity: 1,
    },
  },
  '& .MuiAutocomplete-clearIndicator': {
    display: 'none',
  },
  '& .MuiAutocomplete-popupIndicator': {
    display: 'none',
  },
  '& .MuiAutocomplete-popper': {
    marginTop: '4px',
    '& .MuiPaper-root': {
      borderRadius: '12px',
      backgroundColor: '#F1F2F4 !important',
      boxShadow: 'none',
      '& .MuiAutocomplete-listbox': {
        maxHeight: '132px !important',
        overflowY: 'auto',
        '& .MuiAutocomplete-option': {
          padding: '12px 20px',
          color: '#000',
          fontFamily: 'Barlow',
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '20px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          '&[aria-selected="true"]': {
            backgroundColor: '#E1E4EA',
          },
          '&.Mui-focused': {
            backgroundColor: '#E1E4EA',
          },
        },
      },
    },
  },
  '& .MuiAutocomplete-endAdornment': {
    position: 'absolute',
    right: '18px',
    cursor: 'pointer',
    '& .expand-icon': {
      transition: 'transform 0.3s ease',
      transform: 'rotate(0deg)',
    },
    '&[aria-expanded="true"] .expand-icon': {
      transform: 'rotate(180deg)',
    },
  },
}));

function StateSelection({ setFormState, handleNext }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedState = useSelector(
    (state) => state.wizardLaunch.stateSelection?.answer[0]
  );
  const dynamicData = useSelector(
    (state) => state.wizardLaunch.screenData.select
  );
  const isLoading = useSelector(
    (state) => state.wizardLaunch.dynamicDataLoading
  );
  const error = useSelector((state) => state.wizardLaunch.dynamicDataError);
  const [selectedValue, setSelectedValue] = useState(selectedState?.id || '');
  const [openDialog, setOpenDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [openStateDialog, setOpenStateDialog] = useState(false);
  const [stateMessage, setStateMessage] = useState('');

  // List of states that need special handling
  const specialStates = [
    'California', 'Illinois', 'Guam', 'Louisiana', 'Michigan', 
    'Montana', 'Nevada', 'New Mexico', 'Virginia', 'Colorado'
  ];

  useEffect(() => {
    if (selectedState?.id) {
      setSelectedValue(selectedState.id);
    } else {
      setSelectedValue('');
    }
  }, [selectedState]);

  const handleStateChange = (event, newValue) => {
    const value = newValue?.id || '';
    setSelectedValue(value);

    if (!value) {
      setOpenDialog(true);
      return;
    }

    const selectedAnswer = dynamicData?.options[0].answer.find(
      (ans) => ans.id === value
    );
    
    // Check if selected state is in the special states list
    if (selectedAnswer && specialStates.includes(selectedAnswer.name)) {
      setStateMessage(`Online ELDT theory training is currently not permitted or recognized for CDL certification in your selected ${selectedAnswer.name} state. As a result, we are unable to offer online enrollment to residents pursuing a CDL in this state at this time.`);
      setOpenStateDialog(true);
      return;
    }

    const selectedOption = {
      question_answer_id: dynamicData?.options[0].question_answer_id,
      answer: selectedAnswer ? [selectedAnswer] : [],
      next_question_id: dynamicData?.options[0].next_question_id,
    };

    if (selectedOption) {
      dispatch(setStateSelection(selectedOption));
      setFormState({
        question_id: selectedOption.next_question_id || '',
        userAnswer: {
          question_answer_id: selectedOption.question_answer_id,
          answer: selectedOption.answer,
          next_question_id: selectedOption.next_question_id,
        },
        question_type: 'select',
      });
      handleNext();
    }
  };

  const handleContinueWithState = () => {
    setOpenStateDialog(false);
    
    const selectedAnswer = dynamicData?.options[0].answer.find(
      (ans) => ans.id === selectedValue
    );
    
    const selectedOption = {
      question_answer_id: dynamicData?.options[0].question_answer_id,
      answer: selectedAnswer ? [selectedAnswer] : [],
      next_question_id: dynamicData?.options[0].next_question_id,
    };

    if (selectedOption) {
      dispatch(setStateSelection(selectedOption));
      setFormState({
        question_id: selectedOption.next_question_id || '',
        userAnswer: {
          question_answer_id: selectedOption.question_answer_id,
          answer: selectedOption.answer,
          next_question_id: selectedOption.next_question_id,
        },
        question_type: 'select',
      });
      handleNext();
    }
  };

  const handleBack = () => {
    navigate('/course-selection');
  };
  const handleOpen = () => {
    setOpen(true);
    const input = document.querySelector('.MuiAutocomplete-inputRoot input');
    if (input) {
      input.focus();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <Typography color='error'>
          Error loading states. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className='box-grid-parent'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Typography className='h5-title' variant='h5' gutterBottom>
        {dynamicData?.question_text || 'Select your state'}
      </Typography>

      <FormControl
        className='box-grid-wrap single-grid-wrap'
        sx={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}
      >
        <CustomAutocomplete
          value={
            dynamicData?.options
              ?.flatMap((option) =>
                option.answer?.map((answer) => ({
                  id: answer.id,
                  name: answer.name,
                }))
              )
              .find((item) => item.id === selectedValue) || null
          }
          onChange={handleStateChange}
          ListboxProps={{
            style: { maxHeight: 165 },
            className: 'custom-listbox',
            sx: {
              '& .MuiAutocomplete-option': {
                padding: '10px 20px',
                marginBottom: '4px',
              },
            },
          }}
          disableClearable
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          options={
            dynamicData?.options
              ?.flatMap((option) =>
                option.answer?.map((answer) => ({
                  id: answer.id,
                  name: answer.name,
                }))
              )
              .filter(
                (item, index, self) =>
                  self.findIndex((t) => t.id === item.id) === index
              ) || []
          }
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option, { selected }) => {
            const { key, ...otherProps } = props;
            return (
              <li
                key={key}
                {...otherProps}
                style={{
                  backgroundColor: selected ? '#E1E4EA' : 'transparent',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#E1E4EA")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = selected ? "#E1E4EA" : "transparent")}
              >
                {option.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder='Select'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: '18px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => setOpen(!open)}
                  >
                    <ExpandCircleDownIcon
                      className='expand-icon'
                      sx={{
                        color: '#01418C',
                        fontSize: '28px',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                ),
              }}
            />
          )}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: '#F1F2F4 !important',
                marginTop: '8px',
                borderRadius: '12px',
                boxShadow: 'none',
              },
            },
          }}
          popupIcon={null}
          disablePortal
        />
      </FormControl>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '480px',
            width: '100%',
            position: 'relative',
          },
        }}
      >
        <Box sx={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1 }}>
          <CloseIcon
            onClick={() => setOpenDialog(false)}
            sx={{
              cursor: 'pointer',
              color: '#000',
              fontSize: '24px',
            }}
          />
        </Box>
        <DialogTitle 
          id='alert-dialog-title'
          sx={{
            padding: 0,
            marginBottom: '16px',
            color: '#000',
            fontFamily: 'Barlow',
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: 'normal',
          }}
        >
          State Selection Required
        </DialogTitle>
        <DialogContent sx={{ padding: 0 }}>
          <Typography 
            id='alert-dialog-description'
            sx={{
              color: '#000',
              fontFamily: 'Barlow',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
            }}
          >
            Please select a state before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '24px 0 0 0' }}>
          <Button
            onClick={() => setOpenDialog(false)}
            color='primary'
            variant="contained"
            sx={{
              height: '48px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 600,
              width: '100%',
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Special State Dialog */}
      <Dialog
        className='disclaimers-modal-state'
        open={openStateDialog}
        onClose={() => setOpenStateDialog(false)}
        aria-labelledby='state-dialog-title'
        aria-describedby='state-dialog-description'
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            position: 'relative',
          },
        }}
      >
        {/* Add close button */}
        <IconButton
          aria-label="close"
          onClick={() => setOpenStateDialog(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#000',
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: '#0A2647',
            fontWeight: 'bold',
            fontSize: '24px',
            pb: 1,
          }}
        >
          <img src={Disclaimers} alt='Disclaimers Icon' />
          Disclaimer
        </DialogTitle>
        
        <DialogContent sx={{ py: 0 }}>
          <Box sx={{ py: 2 }}>
            <Typography
              sx={{
                textAlign: 'center',
                fontWeight: '400',
                color: '#000',
                fontSize: '14px',
              }}
            >
              {stateMessage}
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 4,
            px: 3,
            gap: 2,
          }}
        >
          {/* <Button
            className='accept-btn'
            variant='contained'
            onClick={handleContinueWithState}
            sx={{
              backgroundColor: '#0D47A1',
              '&:hover': {
                backgroundColor: '#0A2647',
              },
              textTransform: 'none',
              fontSize: '16px',
              px: 4,
            }}
          >
            Continue
          </Button> */}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StateSelection;
